<script>
import lazyLoadComponent from '@router/lazyload-component'
import Layout from '@layouts/main'
import { getHotelGoodsDetail, createHotelGoods, updateHotelGoods } from '@/src/apis/hotelGoods'
import { getHotelGoodsTags } from '@/src/apis/hotelGoodsTag'
import { getProductsInfo } from '@/src/router/views/hotel/api'

const initSubmitData = () => ({
  name: '',
  description: '',
  image: [],
  status: 'active',
  tagIds: [],
  categoryIds: [],
  productIds: [],
  billingType: 'once',
  priceMode: 'unified',
  prices: [],
})

export default {
  page: { title: '加購商品' },
  components: {
    Layout,
    BaseSteps: () => import('@components/BaseSteps'),
    TabBasicInfo: () => lazyLoadComponent(import('./components/TabBasicInfo')),
    TabPrice: () => lazyLoadComponent(import('./components/TabPrice')),
  },
  props: {
    goodsId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      submitData: initSubmitData(),
      tagList: [],
      categoryList: [],
      productList: [],
      loading: false,
      submitting: false,
      activeStep: 1,
      activeTab: 'basic',
      tryingNextStep: false,
      tabList: [{ title: '基本設定', name: 'basic' }, { title: '價格設定', name: 'price' }],
    }
  },
  computed: {
    isEditMode() {
      return !!this.goodsId
    },
    pageTitle() {
      return this.isEditMode ? '編輯加購商品' : '新增加購商品'
    },
    currentTabTitle() {
      const tab = this.tabList.find((t) => t.name === this.activeTab)
      return tab ? tab.title : ''
    },
  },
  async created() {
    this.loading = true
    try {
      const [tags, categories, products] = await Promise.all([
        getHotelGoodsTags().catch(() => []),
        this.$store.dispatch('product/fetchServiceCategory').catch(() => []),
        getProductsInfo().catch(() => ({})),
      ])
      this.tagList = Array.isArray(tags) ? tags : tags?.items || []
      this.categoryList = Array.isArray(categories) ? categories : categories?.items || []
      this.productList = Array.isArray(products) ? products : products?.items || []
      if (this.isEditMode) {
        await this.fetchDetail()
      }
    } finally {
      this.loading = false
    }
  },
  methods: {
    async fetchDetail() {
      const detail = await getHotelGoodsDetail(this.goodsId)
      this.submitData = {
        name: detail.name || '',
        description: detail.description || '',
        image: detail.image ? [{ path: detail.image }] : [],
        status: detail.status || 'active',
        tagIds: (detail.tags || []).map((t) => t.id),
        categoryIds: detail.categoryIds || [],
        productIds: detail.productIds || [],
        billingType: detail.billingType || 'once',
        priceMode: detail.priceMode || 'unified',
        prices: (detail.prices || []).map((p) => ({
          ...p,
          cost: Number(p.cost) || 0,
          origin: Number(p.origin) || 0,
          price: Number(p.price) || 0,
        })),
      }
    },
    goBack() {
      this.$router.push({ path: '/hotel', query: { tab1: '3' } })
    },
    async onClickNextStep() {
      this.tryingNextStep = true
      try {
        await this.$refs.currentStep.validate()
        this.activeStep++
      } catch {
        // validation failed, stay on current step
      } finally {
        this.tryingNextStep = false
      }
    },
    async beforeTabSwitch(activeName, oldActiveName) {
      try {
        if (oldActiveName === 'basic') {
          await this.$refs.basic.validate()
        }
      } catch (error) {
        this.$message.error('請確認商品名稱已填寫')
        throw new Error('')
      }
    },
    async tryToStore() {
      if (this.isEditMode) {
        try {
          await this.$refs.basic.validate()
        } catch {
          this.activeTab = 'basic'
          return
        }
      }
      this.submitting = true
      try {
        const payload = {
          name: this.submitData.name,
          description: this.submitData.description,
          image: this.submitData.image[0]?.path || '',
          status: this.submitData.status,
          tagIds: this.submitData.tagIds,
          categoryIds: this.submitData.categoryIds,
          productIds: this.submitData.productIds,
          priceMode: this.submitData.priceMode,
          billingType: this.submitData.billingType,
          prices: this.submitData.prices.map((p) => ({
            name: p.name || '',
            ...(p.categoriesIds !== undefined ? { categoriesIds: p.categoriesIds } : {}),
            cost: Number(p.cost) || 0,
            origin: Number(p.origin) || 0,
            price: Number(p.price) || 0,
          })),
        }
        if (this.isEditMode) {
          await updateHotelGoods(this.goodsId, payload)
        } else {
          await createHotelGoods(payload)
        }
        this.$message({ message: '儲存成功', type: 'success' })
        this.goBack()
      } catch (error) {
        console.error(error)
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<template>
  <Layout>
    <div style="margin-bottom: 60px;">
      <!-- ── 新增模式 ───────────────────────────────────── -->
      <template v-if="!isEditMode">
        <BaseSteps :active="activeStep" :list="tabList" />

        <div :class="$style.content">
          <div v-if="loading" v-loading="true" style="min-height: 300px;" />
          <template v-else>
            <TabBasicInfo v-if="activeStep === 1" ref="currentStep" :default-value="submitData" :tag-list="tagList" :category-list="categoryList" :product-list="productList" />
            <TabPrice
              v-else
              ref="currentStep"
              :prices.sync="submitData.prices"
              :price-mode.sync="submitData.priceMode"
              :category-list="categoryList"
              :selected-category-ids="submitData.categoryIds"
              :edit-mode="isEditMode"
            />
          </template>
        </div>

        <div :class="$style.footer">
          <el-button class="is-round" @click="goBack">取消</el-button>
          <el-button v-if="activeStep > 1" class="is-round" @click="activeStep--">
            上一步
          </el-button>
          <el-button
            v-if="activeStep < 2"
            class="is-round"
            type="primary"
            :loading="tryingNextStep"
            :disabled="tryingNextStep || !submitData.name.trim() || !submitData.categoryIds.length || !submitData.productIds.length"
            @click="onClickNextStep"
          >
            下一步
          </el-button>
          <el-button v-if="activeStep === 2" class="is-round" type="primary" :loading="submitting" :disabled="submitting" @click="tryToStore">
            <BaseIcon v-if="!submitting" name="check" />
            建立
          </el-button>
        </div>
      </template>

      <!-- ── 編輯模式 ───────────────────────────────────── -->
      <template v-else>
        <el-tabs v-model="activeTab" type="border-card" class="isStyleBase" :before-leave="beforeTabSwitch">
          <el-tab-pane v-for="tab in tabList" :key="tab.name" :label="tab.title" :name="tab.name" />
        </el-tabs>

        <div :class="$style.pageHeader">
          <el-page-header :content="currentTabTitle" @back="goBack" />
        </div>

        <div :class="$style.tabContent">
          <div v-if="loading" v-loading="true" style="min-height: 300px;" />
          <template v-else>
            <TabBasicInfo ref="basic" v-show="activeTab === 'basic'" :default-value="submitData" :tag-list="tagList" :category-list="categoryList" :product-list="productList" />
            <TabPrice
              ref="price"
              v-show="activeTab === 'price'"
              :prices.sync="submitData.prices"
              :price-mode.sync="submitData.priceMode"
              :category-list="categoryList"
              :selected-category-ids="submitData.categoryIds"
              :edit-mode="isEditMode"
            />
          </template>
        </div>

        <div :class="$style.footer">
          <el-button class="is-round" @click="goBack">取消</el-button>
          <el-button class="is-round" type="primary" :loading="submitting" :disabled="submitting" @click="tryToStore">
            <BaseIcon v-if="!submitting" name="check" />
            儲存
          </el-button>
        </div>
      </template>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@src/design/layout_detail_steps.scss';

.content {
  padding: 20px;
}

.pageHeader {
  display: flex;
  align-items: center;
  margin: 20px;

  :global {
    .el-page-header {
      align-items: center;
      font-size: 22px;
    }
    .el-page-header__left {
      border: 1px solid $color-border-for-bg-white;
      border-radius: $border-radius;
      padding: 12px 24px;
      background-color: white;
    }
  }
}

.tabContent {
  margin: 20px;
}
</style>
